import { type AskPunttSearchProgress } from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  File,
  Globe,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import { useState } from "react";

import { useAnalytics } from "../../../utils/analytics";

interface SearchProgressProps {
  searchProgress: AskPunttSearchProgress[];
  collapsed?: boolean;
  /** When true, results are auto-expanded (during streaming) */
  streaming?: boolean;
}

function SourceIcon({ source }: { source: string }) {
  if (
    source.toLowerCase().includes("regulatory") ||
    source.toLowerCase().includes("puntt")
  ) {
    return <MagnifyingGlass size={14} className="text-puntt-accent-9" />;
  }
  if (
    source.toLowerCase().includes("sharepoint") ||
    source.toLowerCase().includes("claims")
  ) {
    return <File size={14} className="text-amber-600" />;
  }
  return <Globe size={14} className="text-green-600" />;
}

function SearchSourceItem({
  source,
  autoExpand,
}: {
  source: AskPunttSearchProgress;
  autoExpand?: boolean;
}) {
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);
  const analytics = useAnalytics();
  const isDone = source.status === "done";
  const isSearching = source.status === "searching";
  const hasResults = isDone && source.results && source.results.length > 0;

  // Auto-expand when streaming, but allow manual override
  const expanded = manualToggle !== null ? manualToggle : autoExpand && isDone;

  return (
    <div className="space-y-1">
      <button
        onClick={() => {
          const willExpand = !expanded;
          if (willExpand) {
            analytics.capture("ask_puntt_search_source_expanded", {
              source: source.source,
              result_count: source.resultCount ?? 0,
            });
          }
          setManualToggle(willExpand);
        }}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-puntt-neutral-gray-3"
      >
        {isSearching ? (
          <CircleNotch size={14} className="animate-spin text-puntt-accent-9" />
        ) : (
          <CheckCircle size={14} weight="fill" className="text-green-600" />
        )}
        <Text size="1" className="flex-1 text-puntt-neutral-gray-11">
          {isSearching ? "Searching " : ""}
          {source.source}
        </Text>
        {isDone && source.resultCount !== undefined && (
          <Text size="1" className="text-puntt-neutral-gray-9">
            {source.resultCount}{" "}
            {source.resultCount === 1 ? "Result" : "Results"}
          </Text>
        )}
        {hasResults &&
          (expanded ? <CaretDown size={12} /> : <CaretRight size={12} />)}
      </button>

      {expanded && source.results && (
        <div className="ml-6 space-y-0.5 rounded-md border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 p-2">
          {source.results.map((result, idx) => (
            <Flex
              key={idx}
              align="center"
              gap="2"
              className="rounded px-2 py-1 hover:bg-puntt-neutral-gray-3"
            >
              <SourceIcon source={source.source} />
              {result.url ? (
                <a
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 truncate text-puntt-accent-11 hover:underline"
                >
                  <Text
                    size="1"
                    as="span"
                    className="text-puntt-neutral-gray-12"
                  >
                    {result.title}
                  </Text>
                </a>
              ) : (
                <Text
                  size="1"
                  className="flex-1 truncate text-puntt-neutral-gray-12"
                >
                  {result.title}
                </Text>
              )}
              {result.url && (
                <a
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 truncate text-puntt-accent-11 hover:underline"
                  style={{ maxWidth: "200px" }}
                  title={result.url}
                >
                  <Text
                    size="1"
                    as="span"
                    className="text-puntt-neutral-gray-9"
                  >
                    {result.url}
                  </Text>
                </a>
              )}
            </Flex>
          ))}
        </div>
      )}
    </div>
  );
}

export function SearchProgress({
  searchProgress,
  collapsed = false,
  streaming = false,
}: SearchProgressProps) {
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  // Only show sources that are still searching or have at least one result
  const visibleProgress = searchProgress.filter(
    (s) =>
      s.status === "searching" ||
      (s.status === "done" &&
        ((s.resultCount ?? 0) > 0 || (s.results?.length ?? 0) > 0)),
  );
  const allDone = searchProgress.every((s) => s.status === "done");

  if (visibleProgress.length === 0) return null;

  if (isCollapsed && allDone) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className="flex items-center gap-1.5 text-left"
      >
        <Text size="1" className="text-puntt-accent-11 hover:underline">
          Searched {visibleProgress.map((s) => s.source).join(", ")}
        </Text>
        <CaretRight size={10} className="text-puntt-accent-11" />
      </button>
    );
  }

  return (
    <div className="space-y-1">
      {visibleProgress.map((source, idx) => (
        <SearchSourceItem key={idx} source={source} autoExpand={streaming} />
      ))}
      {allDone && !streaming && (
        <button onClick={() => setIsCollapsed(true)} className="mt-1 text-left">
          <Text size="1" className="text-puntt-neutral-gray-9 hover:underline">
            Collapse
          </Text>
        </button>
      )}
    </div>
  );
}
